import React, { useState, useRef, useEffect } from 'react';
import {
  BugPlay,
  ChevronDown,
  ChevronRight,
  FlaskConical,
  Cloud,
  Shield,
  KeyRound,
  Layers,
  CheckCircle,
  X,
  RotateCcw,
} from 'lucide-react';
import type { AwsTestSubhandler } from '../api-utils/tests';

const AWS_TEST_THREAT_OPTIONS: { id: AwsTestSubhandler; label: string }[] = [
  { id: 'public_s3_bucket', label: 'Public S3 bucket' },
  { id: 'iam_no_mfa', label: 'IAM user without MFA' },
  { id: 'stale_access_keys', label: 'Stale access keys' },
  { id: 'open_ssh_sg', label: 'Open SSH security group' },
  { id: 'privilege_escalation', label: 'Privilege escalation' },
];

const CROWDSTRIKE_THREAT_OPTIONS = [
  'Ransomware execution',
  'Credential theft (Mimikatz)',
  'Trojan detection',
  'Lateral movement (PSExec)',
  'Backdoor installation',
];

const OKTA_TEST_THREAT_OPTIONS: { id: string; label: string }[] = [
  { id: 'brute_force_password_spray', label: 'Brute force login' },
  { id: 'admin_role_escalation', label: 'Privilege escalation' },
  { id: 'impossible_travel', label: 'Session hijacking' },
  { id: 'mfa_reset_suspicious_login', label: 'MFA bypass attempt' },
  { id: 'dormant_account_reactivation', label: 'Dormant account reactivation' },
];

const CROSS_DOMAIN_THREAT_OPTIONS = [
  'Cross-cloud credential abuse',
  'Unified exfiltration path',
  'Multi-vector persistence',
];

export interface ThreatTestsControlsProps {
  selectedAwsTests: Set<AwsTestSubhandler>;
  setSelectedAwsTests: React.Dispatch<React.SetStateAction<Set<AwsTestSubhandler>>>;
  selectedCrowdstrikeTests: Set<number>;
  setSelectedCrowdstrikeTests: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedOktaTests: Set<number>;
  setSelectedOktaTests: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedCrossDomainTests: Set<number>;
  setSelectedCrossDomainTests: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedIdsCount: number;
  onMonitorTests: () => void;
  onLaunchAws: () => void;
  onLaunchOkta: () => void;
  onComingSoon: (label: string) => void;
  onCloseSelected: () => void;
  onResetAll: () => void;
  layout?: 'dropdown' | 'panel';
}

const ThreatTestsControls: React.FC<ThreatTestsControlsProps> = ({
  selectedAwsTests,
  setSelectedAwsTests,
  selectedCrowdstrikeTests,
  setSelectedCrowdstrikeTests,
  selectedOktaTests,
  setSelectedOktaTests,
  selectedCrossDomainTests,
  setSelectedCrossDomainTests,
  selectedIdsCount,
  onMonitorTests,
  onLaunchAws,
  onLaunchOkta,
  onComingSoon,
  onCloseSelected,
  onResetAll,
  layout = 'panel',
}) => {
  const [open, setOpen] = useState(layout === 'panel');
  const [submenu, setSubmenu] = useState<'aws' | 'crowdstrike' | 'okta' | 'cross' | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (layout !== 'dropdown') return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSubmenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [layout]);

  const toggleAws = (id: AwsTestSubhandler) => {
    setSelectedAwsTests((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const menu = (
    <div className={`${layout === 'dropdown' ? 'absolute left-0 top-full mt-2 w-56' : 'w-full max-w-md'} bg-c3-panel border border-c3-panel-bd rounded-lg shadow-[var(--shadow-pop)] z-[9999] overflow-visible`}>
      <button
        type="button"
        onClick={() => {
          onMonitorTests();
          setOpen(false);
        }}
        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-c3-white hover:bg-acc/10 transition-colors bg-transparent border-none cursor-pointer"
      >
        <FlaskConical className="w-4 h-4 text-t2" />
        Monitor tests
      </button>

      <Submenu
        label="AWS threats"
        icon={<Cloud className="w-4 h-4 text-t2" />}
        open={submenu === 'aws'}
        onEnter={() => setSubmenu('aws')}
        onLeave={() => setSubmenu(null)}
      >
        <button
          type="button"
          onClick={() =>
            setSelectedAwsTests(
              selectedAwsTests.size === AWS_TEST_THREAT_OPTIONS.length
                ? new Set()
                : new Set(AWS_TEST_THREAT_OPTIONS.map((o) => o.id))
            )
          }
          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-c3-mute hover:bg-acc/10 border-b border-c3-tile-bd bg-transparent border-x-0 border-t-0 cursor-pointer"
        >
          Select all
        </button>
        {AWS_TEST_THREAT_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => toggleAws(option.id)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-c3-mute hover:bg-acc/10 bg-transparent border-none cursor-pointer"
          >
            <CheckBox checked={selectedAwsTests.has(option.id)} />
            {option.label}
          </button>
        ))}
        <div className="border-t border-c3-tile-bd p-2">
          <button
            type="button"
            onClick={onLaunchAws}
            disabled={selectedAwsTests.size === 0}
            className={`w-full py-2 rounded-lg font-medium text-sm ${
              selectedAwsTests.size > 0
                ? 'bg-acc-f text-on-acc'
                : 'bg-s3 text-t3 cursor-not-allowed'
            }`}
          >
            Launch ({selectedAwsTests.size})
          </button>
        </div>
      </Submenu>

      <Submenu
        label="CrowdStrike threats"
        icon={<Shield className="w-4 h-4 text-t2" />}
        open={submenu === 'crowdstrike'}
        onEnter={() => setSubmenu('crowdstrike')}
        onLeave={() => setSubmenu(null)}
      >
        {CROWDSTRIKE_THREAT_OPTIONS.map((label, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() =>
              setSelectedCrowdstrikeTests((prev) => {
                const n = new Set(prev);
                if (n.has(idx)) n.delete(idx);
                else n.add(idx);
                return n;
              })
            }
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-c3-mute hover:bg-acc/10 bg-transparent border-none cursor-pointer"
          >
            <CheckBox checked={selectedCrowdstrikeTests.has(idx)} />
            {label}
          </button>
        ))}
        <div className="border-t border-c3-tile-bd p-2">
          <button
            type="button"
            onClick={() => onComingSoon('Crowdstrike threats coming soon')}
            disabled={selectedCrowdstrikeTests.size === 0}
            className={`w-full py-2 rounded-lg font-medium text-sm ${
              selectedCrowdstrikeTests.size > 0 ? 'bg-acc-f text-on-acc' : 'bg-s3 text-t3 cursor-not-allowed'
            }`}
          >
            Launch ({selectedCrowdstrikeTests.size})
          </button>
        </div>
      </Submenu>

      <Submenu
        label="Okta threats"
        icon={<KeyRound className="w-4 h-4 text-t2" />}
        open={submenu === 'okta'}
        onEnter={() => setSubmenu('okta')}
        onLeave={() => setSubmenu(null)}
      >
        {OKTA_TEST_THREAT_OPTIONS.map((option, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() =>
              setSelectedOktaTests((prev) => {
                const n = new Set(prev);
                if (n.has(idx)) n.delete(idx);
                else n.add(idx);
                return n;
              })
            }
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-c3-mute hover:bg-acc/10 bg-transparent border-none cursor-pointer"
          >
            <CheckBox checked={selectedOktaTests.has(idx)} />
            {option.label}
          </button>
        ))}
        <div className="border-t border-c3-tile-bd p-2">
          <button
            type="button"
            onClick={onLaunchOkta}
            disabled={selectedOktaTests.size === 0}
            className={`w-full py-2 rounded-lg font-medium text-sm ${
              selectedOktaTests.size > 0 ? 'bg-acc-f text-on-acc' : 'bg-s3 text-t3 cursor-not-allowed'
            }`}
          >
            Launch ({selectedOktaTests.size})
          </button>
        </div>
      </Submenu>

      <Submenu
        label="Cross-domain threats"
        icon={<Layers className="w-4 h-4 text-t2" />}
        open={submenu === 'cross'}
        onEnter={() => setSubmenu('cross')}
        onLeave={() => setSubmenu(null)}
      >
        {CROSS_DOMAIN_THREAT_OPTIONS.map((label, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() =>
              setSelectedCrossDomainTests((prev) => {
                const n = new Set(prev);
                if (n.has(idx)) n.delete(idx);
                else n.add(idx);
                return n;
              })
            }
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-c3-mute hover:bg-acc/10 bg-transparent border-none cursor-pointer"
          >
            <CheckBox checked={selectedCrossDomainTests.has(idx)} />
            {label}
          </button>
        ))}
        <div className="border-t border-c3-tile-bd p-2">
          <button
            type="button"
            onClick={() => onComingSoon('Cross-domain threats coming soon')}
            disabled={selectedCrossDomainTests.size === 0}
            className={`w-full py-2 rounded-lg font-medium text-sm ${
              selectedCrossDomainTests.size > 0 ? 'bg-acc-f text-on-acc' : 'bg-s3 text-t3 cursor-not-allowed'
            }`}
          >
            Launch ({selectedCrossDomainTests.size})
          </button>
        </div>
      </Submenu>

      <button
        type="button"
        onClick={onCloseSelected}
        disabled={selectedIdsCount === 0}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm bg-transparent border-none ${
          selectedIdsCount === 0 ? 'text-t3 opacity-60 cursor-not-allowed' : 'text-c3-mute hover:bg-acc/10 cursor-pointer'
        }`}
      >
        <X className="w-4 h-4 text-c3-red" />
        Close selected {selectedIdsCount > 0 && `(${selectedIdsCount})`}
      </button>
      <div className="border-t border-c3-tile-bd">
        <button
          type="button"
          onClick={onResetAll}
          className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-c3-mute hover:bg-crit/10 hover:text-c3-red bg-transparent border-none cursor-pointer"
        >
          <RotateCcw className="w-4 h-4 text-c3-red" />
          Reset all
        </button>
      </div>
    </div>
  );

  if (layout === 'panel') {
    return <div className="relative">{menu}</div>;
  }

  return (
    <div className="relative z-[9999]" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2 bg-[#221e44] hover:brightness-110 text-c3-lpurple border border-c3-purple rounded-lg font-medium text-sm"
      >
        <BugPlay className="w-4 h-4" />
        Threat tests
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && menu}
    </div>
  );
};

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <div
      className={`w-4 h-4 rounded border flex items-center justify-center ${
        checked ? 'bg-c3-purple border-c3-purple' : 'border-t3'
      }`}
    >
      {checked && <CheckCircle className="w-3 h-3 text-on-acc" />}
    </div>
  );
}

function Submenu({
  label,
  icon,
  open,
  onEnter,
  onLeave,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  open: boolean;
  onEnter: () => void;
  onLeave: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-c3-mute hover:bg-acc/10 bg-transparent border-none cursor-pointer"
      >
        {icon}
        {label}
        <ChevronRight className="w-4 h-4 ml-auto" />
      </button>
      {open && (
        <div
          className="absolute left-full top-0 ml-1 w-64 bg-c3-panel border border-c3-panel-bd rounded-lg shadow-[var(--shadow-pop)] z-[10000]"
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default ThreatTestsControls;
